import { devError } from '../utils/devLogger';
import React from 'react';
import { Alert, Button, Container, Row, Col, Card } from 'react-bootstrap';

/**
 * Production-ready Error Boundary Component
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null 
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Generate unique error ID for tracking
    const errorId = `ERR_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Log the error details
    this.logError(error, errorInfo, errorId);
    
    // Update state with error details
    this.setState({
      error,
      errorInfo,
      errorId
    });
  }

  logError = (error, errorInfo, errorId) => {
    const errorDetails = {
      errorId,
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      userId: this.props.userId || 'anonymous',
      version: process.env.REACT_APP_VERSION || 'unknown'
    };

    // Log to console for development
    devError('ErrorBoundary caught an error:', errorDetails);

    // In production, you would send this to your error tracking service
    // Example: Sentry, LogRocket, or custom error tracking
    if (process.env.NODE_ENV === 'production') {
      this.sendErrorToService(errorDetails);
    }
  };

  sendErrorToService = async (errorDetails) => {
    try {
      // Example error tracking service call
      // Replace with your actual error tracking service
      await fetch('/api/errors', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(errorDetails),
      });
    } catch (err) {
      devError('Failed to send error to tracking service:', err);
    }
  };

  handleRetry = () => {
    // Clear the error state to retry rendering
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null 
    });
  };

  handleReload = () => {
    // Reload the entire page
    window.location.reload();
  };

  handleReportIssue = () => {
    // Open issue reporting (email, support ticket, etc.)
    const subject = encodeURIComponent(`Error Report - ${this.state.errorId}`);
    const body = encodeURIComponent(
      `Error ID: ${this.state.errorId}\n` +
      `Timestamp: ${new Date().toISOString()}\n` +
      `Error: ${this.state.error?.message}\n` +
      `Page: ${window.location.href}\n\n` +
      `Please describe what you were doing when this error occurred:`
    );
    
    window.open(`mailto:support@npigovernance.co.za?subject=${subject}&body=${body}`);
  };

  render() {
    if (this.state.hasError) {
      // Production error fallback UI
      return (
        <Container className="mt-5">
          <Row className="justify-content-center">
            <Col lg={8}>
              <Card className="border-danger">
                <Card.Header className="bg-danger text-white">
                  <h4 className="mb-0">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    Something went wrong
                  </h4>
                </Card.Header>
                <Card.Body>
                  <Alert variant="danger" className="mb-4">
                    <Alert.Heading>Application Error</Alert.Heading>
                    <p className="mb-0">
                      We're sorry, but something unexpected happened. Our team has been notified and is working to fix the issue.
                    </p>
                  </Alert>

                  <div className="mb-4">
                    <h6>What can you do?</h6>
                    <ul>
                      <li>Try refreshing the page</li>
                      <li>Check your internet connection</li>
                      <li>Clear your browser cache</li>
                      <li>Try again in a few minutes</li>
                    </ul>
                  </div>

                  {this.state.errorId && (
                    <Alert variant="info" className="mb-4">
                      <small>
                        <strong>Error ID:</strong> {this.state.errorId}
                        <br />
                        <strong>Time:</strong> {new Date().toLocaleString()}
                      </small>
                    </Alert>
                  )}

                  <div className="d-flex gap-2 flex-wrap">
                    <Button variant="primary" onClick={this.handleRetry}>
                      <i className="fas fa-redo me-2"></i>
                      Try Again
                    </Button>
                    
                    <Button variant="secondary" onClick={this.handleReload}>
                      <i className="fas fa-sync me-2"></i>
                      Reload Page
                    </Button>
                    
                    <Button variant="outline-primary" onClick={this.handleReportIssue}>
                      <i className="fas fa-bug me-2"></i>
                      Report Issue
                    </Button>
                    
                    {this.props.onGoHome && (
                      <Button variant="outline-secondary" onClick={this.props.onGoHome}>
                        <i className="fas fa-home me-2"></i>
                        Go to Dashboard
                      </Button>
                    )}
                  </div>

                  {/* Development error details */}
                  {process.env.NODE_ENV === 'development' && this.state.error && (
                    <details className="mt-4">
                      <summary className="btn btn-link p-0">
                        <small>Show technical details (development only)</small>
                      </summary>
                      <div className="mt-2">
                        <Alert variant="warning">
                          <h6>Error Details:</h6>
                          <pre className="small">
                            <strong>Message:</strong> {this.state.error.message}
                            {'\n\n'}
                            <strong>Stack Trace:</strong>{'\n'}
                            {this.state.error.stack}
                            {'\n\n'}
                            <strong>Component Stack:</strong>{'\n'}
                            {this.state.errorInfo?.componentStack}
                          </pre>
                        </Alert>
                      </div>
                    </details>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      );
    }

    return this.props.children;
  }
}

// Higher-order component for easier usage
export const withErrorBoundary = (Component, errorBoundaryProps = {}) => {
  const WrappedComponent = (props) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );
  
  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  return WrappedComponent;
};

// Hook for manual error reporting
export const useErrorHandler = () => {
  const reportError = (error, errorInfo = {}) => {
    const errorId = `MANUAL_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const errorDetails = {
      errorId,
      message: error.message || error,
      stack: error.stack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      manual: true,
      ...errorInfo
    };

    devError('Manual error report:', errorDetails);

    // Send to error tracking service in production
    if (process.env.NODE_ENV === 'production') {
      fetch('/api/errors', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(errorDetails),
      }).catch(err => devError('Failed to send error:', err));
    }

    return errorId;
  };

  return { reportError };
};

export default ErrorBoundary;